import React from 'react';
import { useTranslation } from 'react-i18next';
import { translations } from 'locales/translations';
import CommonModal from 'app/common/modal/CommonModal';
import { getInstruction } from '../../../services/notificationPermission';
import { BROWSER_STEP_ICONS } from './browserIcons';
import { useNotificationGate } from './service';

/**
 * Post-login notification permission gate (CRCN-832).
 *
 * Shown on the landing screen of each role once authentication and navigation
 * have already completed, so nothing in the login path waits on the browser
 * permission prompt. Non-dismissible by design: there is no close button and
 * the mask is inert until permission is granted.
 *
 * Styling deliberately mirrors `app/common/modal/ModalConfirm` so the dialog
 * matches every other modal in the app; that shared component is not reused
 * directly because it always renders a cancel button and caps content at two
 * lines, neither of which fits this dialog.
 */
const NotificationGate: React.FC = () => {
  const { t } = useTranslation();
  const {
    visible,
    permission,
    phase,
    browser,
    stillBlocked,
    needsSettingsChange,
    isBusy,
    onPrimaryAction,
    canDismiss,
    onDismiss,
  } = useNotificationGate();

  const copy = translations.NOTIFICATION;
  const isTimeout = phase === 'timeout';
  const instruction = getInstruction(browser);

  const title = needsSettingsChange
    ? permission === 'unsupported'
      ? t(copy.UNSUPPORTED_TITLE)
      : t(copy.DENIED_TITLE)
    : isTimeout
    ? t(copy.TIMEOUT_TITLE)
    : t(copy.TITLE);

  const description = needsSettingsChange
    ? permission === 'unsupported'
      ? t(copy.UNSUPPORTED_DESCRIPTION)
      : t(copy.DENIED_DESCRIPTION)
    : t(copy.DESCRIPTION);

  const notice = isTimeout
    ? t(copy.TIMEOUT_NOTICE)
    : stillBlocked && permission === 'denied'
    ? t(copy.DENIED_STILL_BLOCKED)
    : stillBlocked && permission === 'unsupported'
    ? t(copy.UNSUPPORTED_STILL_BLOCKED)
    : '';

  const buttonLabel = needsSettingsChange
    ? phase === 'checking'
      ? t(copy.BUTTON_CHECKING)
      : t(copy.BUTTON_RECHECK)
    : phase === 'requesting'
    ? t(copy.BUTTON_REQUESTING)
    : isTimeout
    ? t(copy.BUTTON_RETRY)
    : t(copy.BUTTON_ENABLE);

  // Closing is deliberate: only the close button dismisses the dialog, never a
  // stray click on the backdrop.
  return (
    <CommonModal
      open={visible}
      disableClose={!canDismiss}
      maskClosable={false}
      onClose={onDismiss}
      className="max-w-[calc(100vw-32px)]"
    >
      {/* Width is fluid below the breakpoint so the employee web flow on mobile
          does not overflow horizontally. */}
      <div className="flex flex-col w-full max-w-[552px] sm:w-[552px]">
        <div className="text-xl text-dark-charcoal text-center font-bold">{title}</div>

        <div className="py-6 text-base text-dark-charcoal text-center">{description}</div>

        {/* Also shown on timeout: browsers can suppress the permission prompt
            entirely (Edge and Chrome "quiet notification requests"), and then
            changing the setting by hand is the only way forward. */}
        {(needsSettingsChange || isTimeout) && (
          <div className="flex flex-col gap-3 rounded-md border border-gray-border bg-snow p-4">
            <div className="text-base font-bold text-dark-charcoal">{t(instruction.titleKey)}</div>
            {instruction.stepKeys.map((stepKey, index) => (
              <div key={stepKey} className="flex items-start gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gray-border bg-white text-sm text-dark-charcoal">
                  {index + 1}
                </span>
                <div className="flex flex-col gap-2">
                  <span className="text-base text-dark-charcoal">{t(stepKey)}</span>
                  {index === 0 && !!instruction.firstStepIcons?.length && (
                    <span className="flex items-center gap-2">
                      {instruction.firstStepIcons.map((iconKey) => (
                        <span
                          key={iconKey}
                          className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-border bg-white text-dark-gray"
                        >
                          {BROWSER_STEP_ICONS[iconKey]}
                        </span>
                      ))}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!!notice && (
          <div className="mt-4 border-l-2 border-alert-yellow bg-snow px-4 py-3 text-base text-dark-gray">
            {notice}
          </div>
        )}

        <div className="mt-6 flex items-center justify-center">
          <button
            type="button"
            disabled={isBusy}
            onClick={onPrimaryAction}
            className={`h-10 px-4 justify-center items-center gap-2 inline-flex text-white text-base font-medium leading-none bg-primary rounded-md ${
              isBusy ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {/* Both children are always present and only toggle visibility, so
                clicking never inserts or removes a DOM node inside the button.
                Mixing a conditional element with a sibling text node here made
                React place a node against a reference it no longer owned. */}
            <span
              aria-hidden={!isBusy}
              className={`h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin ${
                isBusy ? 'inline-block' : 'hidden'
              }`}
            />
            <span>{buttonLabel}</span>
          </button>
        </div>

        <div className="mt-4 text-sm text-medium-gray text-center">
          {!canDismiss
            ? t(copy.PERSISTENCE_NOTE)
            : permission === 'unsupported'
            ? t(copy.DISMISSIBLE_NOTE)
            : t(copy.BLOCKED_DISMISSIBLE_NOTE)}
        </div>
      </div>
    </CommonModal>
  );
};

export default NotificationGate;
